import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { InputNumber, Modal, Select, Table, Tag } from "antd";
import { deploymentNormApis } from "../../../api/MachineryEquipment";
import {
  computeDeploymentHours,
  formatHours,
} from "../machinery-norms/deployment-norms/deploymentNormHours";

// Applies DEPLOYMENT norms to a task as a decoupled equipment-crew REQUIREMENT.
// Unlike the machinery (MUCKING) flow there are NO heads and NO rate on the
// norm: each selected norm freezes ONE number — appliedQuantity = task work
// quantity (or the override) — and everything else (equipment lines, hours,
// money) derives LIVE at read time from the version in force + the equipment
// hourly rates. The preview therefore shows HOURS, not amounts: hours per line
// = (qty × AF) / (prodNorm × EUF) × (1 + NPF), the same formula the backend
// derives with. Only the task's OWN project's Active norms are applicable —
// org-master norms must be duplicated into the project first.
// qty 0 is valid; negatives are blocked. Per-norm skips come back in `skipped[]`.

export interface ApplyDeploymentTaskInfo {
  taskId: number;
  taskName?: string;
  workType?: string;
  workQuantity?: number | string | null;
}

interface ApplicableLine {
  assetId: number;
  assetCode?: string;
  assetName?: string;
  assetStatus?: string;
  toolTypeName?: string;
  capacity?: string | null;
  nonProductionHoursFactor?: string | null;
  effectiveUtilizationFactor?: string | null;
  applicationFactor?: string | null;
  uomId?: number | null;
  uomName?: string;
  prodNorm?: string | null;
}

interface ApplicableNorm {
  deploymentNormId: number;
  normCode: string;
  description?: string | null;
  alreadyApplied?: boolean;
  hasVersion?: boolean;
  versionEffectiveFrom?: string | null;
  lines: ApplicableLine[];
}

interface SkippedItem {
  deploymentNormId?: number;
  normCode?: string;
  error?: string;
}

interface ApplyResult {
  applied: number;
  skipped: SkippedItem[];
}

type Stage = "form" | "done";

const round2 = (n: number) => Math.round(n * 100) / 100;

// Factors travel as FRACTIONS ("0.05" = 5%) — display as whole percents.
const fmtPct = (v: any) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 10000) / 100}%`;
};

const mutedStyle: React.CSSProperties = { color: "#b0b7c3" };

const ApplyDeploymentNormModal = ({
  open,
  onClose,
  task,
  onApplied,
  replaceMode = false,
  replaceDeploymentNormIds,
}: {
  open: boolean;
  onClose: () => void;
  task: ApplyDeploymentTaskInfo;
  onApplied?: () => void;
  // Re-apply ("swap on apply"): preselect the current set; on submit remove all
  // current items first, then apply the chosen set. Cancelling leaves them intact.
  replaceMode?: boolean;
  replaceDeploymentNormIds?: number[];
}) => {
  const [stage, setStage] = useState<Stage>("form");
  const [options, setOptions] = useState<ApplicableNorm[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // Applicability context from the backend: the task's work basis.
  const [applicableWorkQty, setApplicableWorkQty] = useState<number | null>(null);
  const [workUom, setWorkUom] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [overrideQty, setOverrideQty] = useState<number | null>(null);
  const [itemsError, setItemsError] = useState("");
  const [qtyError, setQtyError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // True once ANY server mutation landed (e.g. re-apply's removals succeeded
  // but the apply leg failed). Closing then must still refresh the parent —
  // otherwise the task card keeps showing norms that no longer exist.
  const mutatedRef = useRef(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ApplyResult | null>(null);

  // Basis quantity: backend-provided task work quantity (authoritative),
  // falling back to the value already on the task card. Decimals arrive as strings.
  const workQuantity =
    applicableWorkQty != null ? applicableWorkQty : Number(task?.workQuantity) || 0;
  // The basis: override (0 allowed) if entered, else the task work quantity.
  const basisQty = overrideQty != null ? Number(overrideQty) : workQuantity;

  const resetAll = useCallback(() => {
    setStage("form");
    setSelectedIds([]);
    setOverrideQty(null);
    setItemsError("");
    setQtyError("");
    setSubmitting(false);
    setError("");
    setResult(null);
    mutatedRef.current = false;
  }, []);

  const loadItems = useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setLoadingItems(true);
      const res = await deploymentNormApis.getApplicable(taskId);
      if (res?.status === true) {
        const data = res?.data || {};
        const list: any[] = Array.isArray(data.items) ? data.items : [];
        setApplicableWorkQty(
          data.workQuantity != null && data.workQuantity !== ""
            ? Number(data.workQuantity)
            : null,
        );
        setWorkUom(data.workUom || "");
        setOptions(
          list.map((n: any) => ({
            deploymentNormId: n.deploymentNormId,
            normCode: n.normCode,
            description: n.description,
            alreadyApplied: !!n.alreadyApplied,
            hasVersion: !!n.hasVersion,
            versionEffectiveFrom: n.versionEffectiveFrom,
            lines: Array.isArray(n.lines) ? n.lines : [],
          })),
        );
      } else {
        setError(res?.message || "Failed to load the applicable equipment norms");
      }
    } catch {
      setError("Failed to load the applicable equipment norms");
    } finally {
      setLoadingItems(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    if (open) {
      resetAll();
      loadItems();
      // Re-apply: preselect the currently applied set.
      if (replaceMode && replaceDeploymentNormIds?.length) {
        setSelectedIds(replaceDeploymentNormIds.map((id) => Number(id)));
      }
    }
  }, [open, resetAll, loadItems, replaceMode, replaceDeploymentNormIds]);

  const optionMap = useMemo(() => {
    const m = new Map<number, ApplicableNorm>();
    options.forEach((o) => m.set(o.deploymentNormId, o));
    return m;
  }, [options]);

  const selectOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.deploymentNormId,
        label: o.description ? `${o.normCode} — ${o.description}` : o.normCode,
        // In replace mode the applied set is being swapped, so it must stay
        // selectable (it is preselected); otherwise "applied" means locked out.
        disabled: !replaceMode && !!o.alreadyApplied,
      })),
    [options, replaceMode],
  );

  // Rich option row: «code — description» + Applied / no-version tags + line count.
  const renderOption = useCallback(
    (option: any) => {
      const o = optionMap.get(Number(option.value));
      if (!o) return option.label;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </span>
          {o.alreadyApplied && (
            <Tag color="green" style={{ margin: 0, fontSize: 11, lineHeight: "18px" }}>
              Applied
            </Tag>
          )}
          {!o.hasVersion && (
            <Tag color="orange" style={{ margin: 0, fontSize: 11, lineHeight: "18px" }}>
              no version
            </Tag>
          )}
          <span style={{ color: "#8094ae", fontSize: 12, whiteSpace: "nowrap" }}>
            {o.lines.length} equipment
          </span>
        </div>
      );
    },
    [optionMap],
  );

  // Live preview: per selected norm, its in-force lines with CLIENT-computed
  // hours off the basis qty. Deployed lines (prodNorm > 0) earn hours;
  // zero-prodNorm rows are "considered, not deployed". No amounts here — rates
  // resolve server-side after apply.
  const previewRows = useMemo(
    () =>
      selectedIds.map((id) => {
        const o = optionMap.get(id);
        const qty = round2(basisQty);
        const lines = (o?.lines || []).map((line, index) => {
          const hours = computeDeploymentHours(qty, line);
          const deployed = Number(line.prodNorm) > 0;
          return { key: `${id}-${line.assetId}-${index}`, ...line, hours, deployed };
        });
        const totalHours = round2(
          lines.reduce((sum, l) => sum + (l.hours ?? 0), 0),
        );
        const deployedCount = lines.filter((l) => l.deployed).length;
        return {
          key: id,
          normCode: o?.normCode || `#${id}`,
          description: o?.description || "",
          hasVersion: o?.hasVersion !== false,
          versionEffectiveFrom: o?.versionEffectiveFrom,
          qty,
          lines,
          deployedCount,
          totalHours,
        };
      }),
    [selectedIds, optionMap, basisQty],
  );

  const totalHours = useMemo(
    () => round2(previewRows.reduce((sum, r) => sum + (r.totalHours || 0), 0)),
    [previewRows],
  );

  const validate = () => {
    let ok = true;
    if (!selectedIds.length) {
      setItemsError("Please select at least one equipment norm");
      ok = false;
    } else {
      setItemsError("");
    }
    // 0 is allowed; only negatives are rejected.
    if (overrideQty != null && Number(overrideQty) < 0) {
      setQtyError("Override quantity cannot be negative");
      ok = false;
    } else {
      setQtyError("");
    }
    return ok;
  };

  const handleApply = async () => {
    if (!validate()) return;
    setError("");
    try {
      setSubmitting(true);
      // Re-apply (swap on apply): remove the current set first so the new
      // selection replaces it. Done here (not on button click) so cancelling
      // BEFORE submit leaves the current set intact.
      if (replaceMode && replaceDeploymentNormIds?.length) {
        for (const id of replaceDeploymentNormIds) {
          const rm = await deploymentNormApis.removeFromTask({
            taskId: Number(task.taskId),
            deploymentNormId: Number(id),
            reason: "Re-applied",
          });
          if (rm?.status === true) mutatedRef.current = true;
          if (rm?.status !== true) {
            setError(
              rm?.data?.message ||
                rm?.message ||
                "Failed to remove the current equipment norm",
            );
            setSubmitting(false);
            return;
          }
        }
      }
      const res = await deploymentNormApis.applyToTask({
        taskId: Number(task.taskId),
        deploymentNormIds: selectedIds.map((id) => Number(id)),
        ...(overrideQty != null ? { overrideQty: Number(overrideQty) } : {}),
      });

      if (res?.status === true) {
        const data = res?.data || {};
        setResult({
          applied: Number(data.applied) || 0,
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
        });
        setStage("done");
        return;
      }

      const rawMessage = res?.data?.message ?? res?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join("; ")
        : rawMessage || "Failed to apply the equipment norm. Please try again.";
      setError(message);
    } catch (err: any) {
      setError(err?.message || "Something went wrong while applying the equipment norm");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onApplied?.();
    onClose();
  };

  // Every non-done close path routes through here: if the failed re-apply
  // already removed rows on the server, the parent must refetch anyway.
  const handleClose = () => {
    if (mutatedRef.current) onApplied?.();
    onClose();
  };

  // Expanded mini-table: one row per equipment line of the norm's in-force
  // matrix, with the client-computed hours. Non-deployed rows are muted.
  const renderLines = (norm: any) => (
    <Table
      size="small"
      pagination={false}
      rowKey="key"
      dataSource={norm.lines}
      columns={[
        {
          title: "Equipment",
          key: "asset",
          ellipsis: true,
          render: (_: any, l: any) => (
            <span style={l.deployed ? undefined : mutedStyle}>
              <span style={{ fontWeight: 500 }}>{l.assetCode || `#${l.assetId}`}</span>
              {l.assetName ? ` — ${l.assetName}` : ""}
              {!l.deployed && (
                <div style={{ fontSize: 11, ...mutedStyle }}>
                  considered, not deployed
                </div>
              )}
            </span>
          ),
        },
        {
          title: "Prod. Norm",
          key: "prodNorm",
          width: 120,
          render: (_: any, l: any) => (
            <span style={l.deployed ? undefined : mutedStyle}>
              {l.prodNorm != null && l.prodNorm !== "" ? Number(l.prodNorm) : "—"}
              {l.deployed && l.uomName ? (
                <span style={{ color: "#8094ae", fontSize: 11 }}>
                  {" "}
                  {l.uomName}/Hr
                </span>
              ) : null}
            </span>
          ),
        },
        {
          title: "NPF",
          key: "npf",
          width: 70,
          render: (_: any, l: any) => (
            <span style={l.deployed ? undefined : mutedStyle}>
              {fmtPct(l.nonProductionHoursFactor)}
            </span>
          ),
        },
        {
          title: "EUF",
          key: "euf",
          width: 70,
          render: (_: any, l: any) => (
            <span style={l.deployed ? undefined : mutedStyle}>
              {fmtPct(l.effectiveUtilizationFactor)}
            </span>
          ),
        },
        {
          title: "AF",
          key: "af",
          width: 70,
          render: (_: any, l: any) => (
            <span style={l.deployed ? undefined : mutedStyle}>
              {fmtPct(l.applicationFactor)}
            </span>
          ),
        },
        {
          title: "Hours",
          key: "hours",
          width: 90,
          align: "right" as const,
          render: (_: any, l: any) => (
            <span style={l.deployed ? { fontWeight: 500 } : mutedStyle}>
              {formatHours(l.hours)}
            </span>
          ),
        },
      ]}
    />
  );

  const previewColumns = [
    {
      title: "Norm",
      key: "normCode",
      ellipsis: true,
      render: (_: any, r: any) => (
        <span>
          <span style={{ fontWeight: 500 }}>{r.normCode}</span>
          {!r.hasVersion && (
            <Tag color="orange" style={{ marginLeft: 6, fontSize: 11, lineHeight: "18px" }}>
              no version
            </Tag>
          )}
          {r.description && (
            <div
              style={{
                color: "#9ca3af",
                fontSize: 11,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.description}
            </div>
          )}
        </span>
      ),
    },
    {
      title: "Equipment",
      key: "equipment",
      width: 110,
      render: (_: any, r: any) =>
        r.lines.length ? (
          <span className="text-soft" style={{ fontSize: 12.5 }}>
            {r.deployedCount} of {r.lines.length} deployed
          </span>
        ) : (
          "—"
        ),
    },
    { title: "Qty", dataIndex: "qty", key: "qty", width: 80 },
    {
      title: "Total Hrs",
      key: "totalHours",
      width: 90,
      align: "right" as const,
      render: (_: any, r: any) => <strong>{r.totalHours}</strong>,
    },
  ];

  const noApplicable = !loadingItems && options.length === 0;

  return (
    <Modal
      title={`${replaceMode ? "Re-apply Equipment Norm" : "Apply Equipment Norm"}${
        task?.taskName ? ` — ${task.taskName}` : ""
      }`}
      open={open}
      footer={null}
      onCancel={stage === "done" ? handleDone : handleClose}
      maskClosable={false}
      width={700}
    >
      {stage === "form" && (
        <div>
          <p className="mb-2">
            Applying an equipment norm records its machine crew as a
            requirement on this task. Only the <em>quantity</em> is frozen (task
            work quantity, or the override) — equipment lines, hours and amounts
            stay live, derived from the norm's version in force and the
            equipment hourly rates. A norm can be applied to a task only once —
            to re-apply, remove it (with a reason) first.
          </p>
          <p className="mb-3" style={{ fontSize: 13 }}>
            <Tag color="blue">
              Basis: {round2(basisQty)}
              {workUom ? ` ${workUom}` : ""}
            </Tag>
          </p>

          <div className="form-group">
            <label className="form-label">
              Equipment Norms <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              placeholder="Select one or more equipment norms"
              showSearch
              optionFilterProp="label"
              loading={loadingItems}
              options={selectOptions as any}
              optionRender={renderOption}
              value={selectedIds}
              onChange={(value) => {
                setSelectedIds(value as number[]);
                setItemsError("");
              }}
              disabled={submitting || noApplicable}
              status={itemsError ? "error" : undefined}
            />
            {itemsError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {itemsError}
              </span>
            )}
            {noApplicable && (
              <Alert
                color="light"
                className="py-2 mt-2 mb-0"
                isOpen
                style={{ textAlign: "left", fontSize: "13px" }}
              >
                No equipment norms in this task's project — org-master norms
                are not directly applicable. Duplicate them into the project
                first from <strong>Norms → Machinery → Equipment Norms</strong>{" "}
                (org-master scope → <em>Duplicate to Project</em>).
              </Alert>
            )}
          </div>

          <div className="form-group mt-2">
            <label className="form-label">
              Override Quantity{" "}
              <span className="text-soft" style={{ fontWeight: 400 }}>
                (optional — replaces the task work quantity; 0 allowed)
              </span>
            </label>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Defaults to task work quantity (${workQuantity})`}
              value={overrideQty}
              onChange={(value) => {
                setOverrideQty(value as number | null);
                setQtyError("");
              }}
              disabled={submitting || noApplicable}
              status={qtyError ? "error" : undefined}
            />
            {qtyError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {qtyError}
              </span>
            )}
          </div>

          {/* Basis 0 is valid but almost always unintended: every line derives
              0 hours until the norm is re-applied with a real quantity. */}
          {!noApplicable && basisQty === 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              isOpen
              style={{ textAlign: "left", fontSize: "13px" }}
            >
              The basis quantity is <strong>0</strong>
              {overrideQty == null ? " (the task has no work quantity)" : ""} —
              the norms will be applied with quantity 0 and derive no hours or
              amounts until re-applied with a quantity.
            </Alert>
          )}

          {previewRows.length > 0 && (
            <div className="mt-3">
              <label className="form-label">Preview</label>
              <Table
                size="small"
                pagination={false}
                columns={previewColumns as any}
                dataSource={previewRows}
                scroll={{ y: 260 }}
                expandable={{
                  rowExpandable: (r: any) => r.lines.length > 0,
                  expandedRowRender: renderLines,
                }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong>Total Working Hours</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong>{totalHours}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
              <div className="text-soft mt-1" style={{ fontSize: 12 }}>
                <Icon name="info" style={{ marginRight: 4 }} />
                Amounts are computed after apply from live equipment rates.
              </div>
            </div>
          )}

          <Alert
            isOpen={!!error}
            color="danger"
            className="py-1 mt-3 mb-0"
            style={{ textAlign: "left", fontSize: "14px" }}
          >
            {error}
          </Alert>

          <div className="d-flex mt-4" style={{ gap: 12 }}>
            {submitting ? (
              <Button color="primary" type="button">
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button color="primary" onClick={handleApply} disabled={noApplicable}>
                <Icon name="check" />
                <span>Apply Equipment Norm</span>
              </Button>
            )}
            {!submitting && (
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={handleClose}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <div>
          <div className="text-center py-2">
            <Icon name="check-circle" style={{ fontSize: 42, color: "#1ee0ac" }} />
            <h6 className="mt-2">{result.applied} equipment norm(s) applied</h6>
          </div>
          {result.skipped.length > 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              style={{ textAlign: "left", fontSize: "14px" }}
            >
              <strong>Skipped norms ({result.skipped.length})</strong>
              <ul className="mt-1 mb-0" style={{ paddingLeft: 18 }}>
                {result.skipped.map((skip, index) => (
                  <li key={index}>
                    <strong>{skip.normCode || `Norm #${skip.deploymentNormId}`}</strong>
                    {skip.error ? ` — ${skip.error}` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApplyDeploymentNormModal;
